import { GoalType } from 'Generated/graphql-types'

const config = { domain: 'Impact', subdomain: 'Goals' }

interface UserAccountDetails {
  userId: number
  userEmail: string
  userAccountType: string
  orgId: number
  funderOrgId: number
  platformPlan: string
}

interface TemplatesTabFilterSubmitted {
  userAccountDetails: UserAccountDetails
  filterCategoryOptionSelected: string
}

interface TemplatesTabCreateGoalClicked {
  userAccountDetails: UserAccountDetails
  goalTemplateId: number
  goalTemplateCategory: string
}

interface GoalTabClicked {
  userAccountType: string
  platformPlan: string
}

interface DeleteGoalSubmitted {
  userAccountType: string
  platformPlan: string
  goalId: number
}

interface TabTasksClicked {
  userAccountType: string
  platformPlan: string
  goalId: number
}

interface TabCommentsClicked {
  userAccountType: string
  platformPlan: string
  goalId: number
}

interface TabChartsClicked {
  userAccountType: string
  platformPlan: string
  goalId: number
}

interface GoalTabFilterClicked {
  userAccountType: string
  platformPlan: string
  goalId: number
}

interface GoalTabFilterSubmitted {
  userAccountType: string
  platformPlan: string
  goalId: number
  filterCategoryOptionSelected: string
}

interface TabBackClicked {
  userAccountType: string
  platformPlan: string
  goalId: number
}

interface GoalSharedWithFunder {
  goalTitle: string
  goalUpdatedDate: string
  isGoalSharedWithFunder: boolean
  relationship: string
  role: string
  userAccountDetails: UserAccountDetails
}

interface CreateGoalClicked {
  role: string
  relationship: string
}

interface ViewOrHideGoalDetailsClicked {
  userId: number
  goalId: number
  goalType: GoalType
  isGoalSharedWithFunder: boolean
}

interface GoalTaskSaveClicked {
  dueDate: string | null
  resourceId: string | null
  taskDescription: string
  taskId: number | null
  taskStatus: string
  taskUpdatedDate: string
  goalId: string
  goalType: string
  linkText: string | null
  linkUrl: string | null
  isGoalSharedWithFunder: boolean
}

interface GoalTaskCompleted {
  assigneeUserEmail: string
  goalId: number
  goalType: GoalType
  initiativeId: number
  resourceId: string | null
  taskDescription: string
  taskId: number
  taskStatus: boolean
  taskUpdatedDate: string
  templateLibraryId: number | null
  userId: number
  dueDate: string | null
  isGoalSharedWithFunder: boolean
}

interface GoalTasksProgress {
  userId: number
  goalId: number
  initiativeId: number
  goalUpdatedDate: string
  goalType: GoalType
  templateLibraryId: number | null
  goalProgress: number
}

interface GoalTaskAssigned {
  assigneeUserId: number
  assigneeUserEmail: string
  assignerUserId: number
  goalId: number
  goalType: GoalType
  initiativeId: number
  taskDescription: string
  taskId: number
  taskStatus: string
  taskUpdatedDate: string
  templateLibraryId: number | null
  userId: number
  goalUpdatedDate: string
  goalTaskAssignationStatus: string
  dueDate: string | null
  isGoalSharedWithFunder: boolean
}

interface AddQualitativeUpdateSubmitted {
  goalUpdateId: number
  isGoalSharedWithFunder: boolean
}

interface CreateGoalChartSubmitted {
  goalId: number
  goalTitle: string
  goalUpdatedDate: string
}

interface CreateDataPointSubmitted {
  dataPointNumber: number
  initiativeName: string
  reportedMetricEntryId: number
  reportedMetricTitle: string
  reportedMetricUpdatedDate: string
  goalId?: number
}

interface RecurringTaskEvent extends GoalTaskSaveClicked {
  assigneeUserEmail: string
}

interface SelectRecurringCadence extends RecurringTaskEvent {
  cadence: string
}

export const goals = {
  Templates_Tab_Clicked: {
    Templates_Tab_Clicked: (param: UserAccountDetails) => param,
    config,
  },
  Templates_Tab_Filter_Clicked: {
    Templates_Tab_Filter_Clicked: (param: UserAccountDetails) => param,
    config,
  },
  Templates_Tab_Filter_Submitted: {
    Templates_Tab_Filter_Submitted: (param: TemplatesTabFilterSubmitted) =>
      param,
    config,
  },
  Templates_Tab_CreateGoal_Clicked: {
    Templates_Tab_CreateGoal_Clicked: (param: TemplatesTabCreateGoalClicked) =>
      param,
    config,
  },
  Goal_Tab_Clicked: {
    Goal_Tab_Clicked: (param: GoalTabClicked) => param,
    config,
  },
  Delete_Goal_Submitted: {
    Delete_Goal_Submitted: (param: DeleteGoalSubmitted) => param,
    config,
  },
  Tab_Tasks_Clicked: {
    Tab_Tasks_Clicked: (param: TabTasksClicked) => param,
    config,
  },
  Tab_Comments_Clicked: {
    Tab_Comments_Clicked: (param: TabCommentsClicked) => param,
    config,
  },
  Tab_Charts_Clicked: {
    Tab_Charts_Clicked: (param: TabChartsClicked) => param,
    config,
  },
  Goal_Tab_Filter_Clicked: {
    Goal_Tab_Filter_Clicked: (param: GoalTabFilterClicked) => param,
    config,
  },
  Goal_Tab_Filter_Submitted: {
    Goal_Tab_Filter_Submitted: (param: GoalTabFilterSubmitted) => param,
    config,
  },
  Tab_Back_Clicked: {
    Tab_Back_Clicked: (param: TabBackClicked) => param,
    config,
  },
  Goal_Share_Funder_Toggle_Clicked: {
    Goal_Share_Funder_Toggle_Clicked: (param: GoalSharedWithFunder) => param,
    config,
  },
  Create_Goal_Clicked: {
    Create_Goal_Clicked: (param: CreateGoalClicked) => param,
    config,
  },
  View_Goal_Details_Clicked: {
    View_Goal_Details_Clicked: (param: ViewOrHideGoalDetailsClicked) => param,
    config,
  },
  Goal_Task_Save_Clicked: {
    Goal_Task_Save_Clicked: (param: GoalTaskSaveClicked) => param,
    config,
  },
  Goal_Task_Completed: {
    Goal_Task_Completed: (param: GoalTaskCompleted) => param,
    config,
  },
  Goal_Tasks_All_Completed: {
    Goal_Tasks_All_Completed: (param: GoalTasksProgress) => param,
    config,
  },
  Goal_Tasks_Halfway_Completed: {
    Goal_Tasks_Halfway_Completed: (param: GoalTasksProgress) => param,
    config,
  },
  Goal_Task_Assigned: {
    Goal_Task_Assigned: (param: GoalTaskAssigned) => param,
    config,
  },
  Add_Qualitative_Update_Submitted: {
    Add_Qualitative_Update_Submitted: (param: AddQualitativeUpdateSubmitted) =>
      param,
    config,
  },
  Goal_Create_Chart_Submitted: {
    Goal_Create_Chart_Submitted: (param: CreateGoalChartSubmitted) => param,
    config,
  },
  Create_Data_Point_Submitted: {
    Data_Point_Submitted: (param: CreateDataPointSubmitted) => param,
    config,
  },
  Selects_Recurring_Cadence: {
    Selects_Recurring_Cadence: (param: SelectRecurringCadence) => param,
    config,
  },
  Edit_Recurring_Task_Clicked: {
    Edit_Recurring_Task_Clicked: (param: RecurringTaskEvent) => param,
    config,
  },
  Duplicate_Recurring_Task_Clicked: {
    Duplicate_Recurring_Task_Clicked: (param: RecurringTaskEvent) => param,
    config,
  },
  Delete_Recurring_Task_Clicked: {
    Delete_Recurring_Task_Clicked: (param: RecurringTaskEvent) => param,
    config,
  },
  Delete_Recurring_Task_Option_Saved: {
    Delete_Recurring_Task_Option_Saved: (
      param: RecurringTaskEvent & { deleteOption: string }
    ) => param,
    config,
  },
  Delete_Task: {
    Delete_Task: (param: { taskId: string }) => param,
    config,
  },
  Unassigned_Clicked: {
    Unassigned_Clicked: (param: TabTasksClicked) => param,
    config,
  },
  Past_Due_Clicked: {
    Past_Due_Clicked: (param: TabTasksClicked) => param,
    config,
  },
  To_Do_Clicked: {
    To_Do_Clicked: (param: TabTasksClicked) => param,
    config,
  },
  Completed_Clicked: {
    Completed_Clicked: (param: TabTasksClicked) => param,
    config,
  },
  Assigned_Me_Clicked: {
    Assigned_Me_Clicked: (param: TabTasksClicked) => param,
    config,
  },
  All_Clicked: {
    All_Clicked: (param: TabTasksClicked) => param,
    config,
  },
}
